import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { taxRateApi } from '../services/friendlyApi';
import type { TaxRateCreate, TaxRateUpdate, ListTaxRatesParams } from '../types';

export const TAX_RATES_QUERY_KEY = 'tax-rates';

export function useTaxRates(params?: ListTaxRatesParams) {
  return useQuery({
    queryKey: [TAX_RATES_QUERY_KEY, params],
    queryFn: () => taxRateApi.list(params),
  });
}

export function useTaxRate(id: string) {
  return useQuery({
    queryKey: [TAX_RATES_QUERY_KEY, id],
    queryFn: () => taxRateApi.get(id),
    enabled: !!id,
  });
}

export function useCreateTaxRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TaxRateCreate) => taxRateApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TAX_RATES_QUERY_KEY] });
    },
  });
}

export function useUpdateTaxRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TaxRateUpdate }) =>
      taxRateApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TAX_RATES_QUERY_KEY] });
    },
  });
}

export function useDeleteTaxRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => taxRateApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TAX_RATES_QUERY_KEY] });
    },
  });
}

export function useInitializeDefaultTaxRates() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => taxRateApi.initializeDefaults(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TAX_RATES_QUERY_KEY] });
    },
  });
}
